import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Star, Trophy, Search, ShieldCheck } from "lucide-react";

const MEDAL = ["bg-amber-100 text-amber-900 border-amber-200",
               "bg-zinc-100 text-zinc-700 border-zinc-200",
               "bg-orange-100 text-orange-900 border-orange-200"];

function Stars({ n }) {
  const v = Number(n) || 0;
  return (
    <div className="flex items-center gap-0.5">
      {[1,2,3,4,5].map((i) => (
        <Star key={i} className={"h-3.5 w-3.5 " + (i <= Math.round(v) ? "text-amber-400 fill-amber-400" : "text-zinc-300")} />
      ))}
    </div>
  );
}

export default function PublicVendorsPage() {
  const { propertyId } = useProperty();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");

  useEffect(() => {
    if (!propertyId) return;
    (async () => {
      const { data } = await api.get(`/vendors/leaderboard?property_id=${propertyId}`);
      setItems(data.items || []);
    })();
  }, [propertyId]);

  const categories = useMemo(() => {
    const s = new Set(items.map((x) => x.category).filter(Boolean));
    return ["all", ...Array.from(s)];
  }, [items]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return items.filter((v) => {
      if (cat !== "all" && v.category !== cat) return false;
      if (!needle) return true;
      return (v.name || "").toLowerCase().includes(needle)
          || (v.category || "").toLowerCase().includes(needle);
    });
  }, [items, q, cat]);

  return (
    <div>
      <PageHeader
        eyebrow="Community"
        title="Vendor Leaderboard"
        subtitle="Ratings from neighbors who've actually worked with them — pick winning bids with confidence."
      />

      <div className="flex flex-col sm:flex-row gap-2 mb-5">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
          <Input
            data-testid="vendor-search"
            placeholder="Search vendors…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 bg-white"
          />
        </div>
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger className="w-52 bg-white" data-testid="vendor-category">
            <SelectValue placeholder="All categories" />
          </SelectTrigger>
          <SelectContent>
            {categories.map((c) => <SelectItem key={c} value={c}>{c === "all" ? "All categories" : c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filtered.map((v, i) => {
          const medal = i < 3 ? MEDAL[i] : null;
          return (
            <Card key={v.id} className="p-5 bg-white" data-testid={`vendor-${v.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    {medal && (
                      <Badge className={"border " + medal}>
                        <Trophy className="h-3 w-3 mr-1 inline" /> #{i + 1}
                      </Badge>
                    )}
                    {!v.active && <Badge variant="outline" className="text-zinc-500">Inactive</Badge>}
                    <Badge variant="outline">{v.category || "General"}</Badge>
                  </div>
                  <div className="text-lg font-semibold font-display mt-1">{v.name}</div>
                  <div className="flex items-center gap-2 mt-1">
                    <Stars n={v.rating} />
                    <div className="text-sm text-zinc-700 tabular-nums font-medium">
                      {v.rating !== null ? Number(v.rating).toFixed(1) : "—"}
                    </div>
                    <div className="text-xs text-zinc-500">
                      · {v.reviews_count} review{v.reviews_count === 1 ? "" : "s"}
                    </div>
                  </div>
                </div>
              </div>
              {v.latest_review ? (
                <div className="mt-3 rounded-md bg-zinc-50 border border-black/5 p-3">
                  <div className="flex items-center gap-2 text-xs text-zinc-500">
                    <Stars n={v.latest_review.stars} />
                    <span>— {v.latest_review.reviewer_name}</span>
                  </div>
                  {v.latest_review.comment && (
                    <div className="text-sm text-zinc-800 mt-1 italic">"{v.latest_review.comment}"</div>
                  )}
                </div>
              ) : (
                <div className="mt-3 text-xs text-zinc-500 flex items-center gap-1">
                  <ShieldCheck className="h-3 w-3" /> No reviews yet — be the first once your ticket is resolved.
                </div>
              )}
            </Card>
          );
        })}
        {filtered.length === 0 && <div className="text-sm text-muted-foreground">No vendors match.</div>}
      </div>
    </div>
  );
}
